/**
 * ToastContext - Manages toast notifications across the app
 * 
 * Features:
 * - Multiple toast stacking
 * - Auto-dismiss with countdown
 * - Helper methods for common toast types
 * - Verification request toast helper
 */

import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  /**
   * Generate unique toast ID
   */
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }, []);

  /**
   * Show a toast notification
   * @param {Object} config - Toast configuration
   * @returns {string} Toast ID
   */
  const showToast = useCallback((config) => {
    const id = config.id || generateId();
    
    const toast = {
      id,
      type: config.type || 'info',
      title: config.title,
      message: config.message,
      actions: config.actions || [],
      countdown: config.countdown || 0,
      position: config.position || 'top-left',
      showCloseButton: config.showCloseButton !== undefined ? config.showCloseButton : true,
      onDismiss: config.onDismiss,
    };

    setToasts(prev => [...prev, toast]);
    return id;
  }, [generateId]);

  /**
   * Dismiss a toast by ID
   */
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  /**
   * Show info toast
   */
  const showInfo = useCallback((title, message) => {
    return showToast({
      type: 'info',
      title,
      message,
    });
  }, [showToast]);

  /**
   * Show success toast
   */
  const showSuccess = useCallback((title, message) => {
    return showToast({
      type: 'success',
      title,
      message,
    });
  }, [showToast]);

  /**
   * Show warning toast
   */
  const showWarning = useCallback((title, message) => {
    return showToast({
      type: 'warning',
      title,
      message,
    });
  }, [showToast]);

  /**
   * Show error toast
   */
  const showError = useCallback((title, message) => {
    return showToast({
      type: 'error',
      title,
      message,
    });
  }, [showToast]);

  /**
   * Show verification request toast
   * Helper specifically for device verification requests
   * 
   * @param {Object} config - Verification toast config
   * @param {string} config.requestId - Verification request ID
   * @param {string} config.deviceName - Name of the device requesting verification
   * @param {Function} config.onVerify - Callback when user clicks "Verify"
   * @param {Function} config.onIgnore - Callback when user clicks "Ignore"
   * @param {number} config.timeout - Timeout in milliseconds (default: 5 minutes)
   */
  const showVerificationRequest = useCallback((config) => {
    const {
      requestId,
      deviceName,
      onVerify,
      onIgnore,
      timeout = 300000, // 5 minutes default
    } = config;

    const countdownSeconds = Math.floor(timeout / 1000);

    return showToast({
      id: requestId,
      type: 'verification',
      title: 'Verification Request',
      message: `${deviceName} wants to verify`,
      actions: [
        {
          label: 'Ignore',
          onClick: onIgnore,
          primary: false,
          showCountdown: true,
        },
        {
          label: 'Verify',
          onClick: onVerify,
          primary: true,
        },
      ],
      countdown: countdownSeconds,
      position: 'top-left',
      showCloseButton: false, // Don't allow manual close for verification requests
      onDismiss: () => {
        // Call onIgnore when auto-dismissed by timeout
        onIgnore?.();
      },
    });
  }, [showToast]);

  const value = {
    toasts,
    showToast,
    dismissToast,
    dismissAll,
    showInfo,
    showSuccess,
    showWarning,
    showError,
    showVerificationRequest,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
